import { Link, createFileRoute } from "@tanstack/react-router";
import { Trans } from "@lingui/react/macro";

import LandingLayout from "../../components/LandingLayout";
import { blogPosts } from "../../lib/blog";

export const Route = createFileRoute("/blog/")({
  component: BlogIndexPage,
  head: () => ({
    meta: [
      {
        title: "BodaWeb — Blog",
      },
      {
        content:
          "Guías, ejemplos y consejos para crear tu invitación de boda digital con BodaWeb.",
        name: "description",
      },
    ],
  }),
});

const dateFormatter = new Intl.DateTimeFormat("es-ES", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatPostDate(iso: string): string {
  // Posts use YYYY-MM-DD in the frontmatter — parse as a calendar date so the
  // displayed value isn't off-by-one in negative-UTC timezones.
  const [year, month, day] = iso
    .split("-")
    .map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return iso;
  }
  return dateFormatter.format(new Date(year, month - 1, day));
}

function BlogIndexPage() {
  return (
    <LandingLayout showFooter>
      <main className="overflow-x-hidden">
        <section className="border-b border-[var(--color-border)] bg-white">
          <div className="mx-auto max-w-4xl px-4 pb-14 pt-28 sm:px-6 sm:pb-16 sm:pt-32">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[var(--color-muted)]">
              <Trans>Blog</Trans>
            </p>
            <h1 className="mt-4 max-w-3xl font-display text-5xl italic leading-[0.92] tracking-[-0.03em] text-[var(--color-ink)] sm:text-6xl">
              <Trans>Ideas, guías y bodas reales.</Trans>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--color-muted)]">
              <Trans>
                Publicamos lo que aprendemos acompañando a parejas que lanzan su
                invitación con BodaWeb: tutoriales, ejemplos visuales y los
                detalles que hacen que una web de boda se sienta tuya.
              </Trans>
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:py-16">
          {blogPosts.length === 0 ? (
            <p
              className="text-base leading-7 text-[var(--color-muted)]"
              data-testid="blog-empty-state"
            >
              <Trans>
                Estamos preparando los primeros artículos. Vuelve en unos días.
              </Trans>
            </p>
          ) : (
            <ul
              className="divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]"
              data-testid="blog-post-list"
            >
              {blogPosts.map((post) => (
                <li key={post.slug}>
                  <Link
                    className="group block cursor-pointer py-8 transition-colors hover:bg-[var(--color-page-soft)]"
                    data-testid={`blog-post-link-${post.slug}`}
                    params={{ slug: post.slug }}
                    to="/blog/$slug"
                  >
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-muted)]">
                      {formatPostDate(post.date)}
                    </p>
                    <h2 className="mt-3 font-display text-3xl italic leading-tight text-[var(--color-ink)] transition-colors group-hover:text-[var(--color-accent)] sm:text-4xl">
                      {post.title}
                    </h2>
                    {post.description ? (
                      <p className="mt-3 max-w-2xl text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                        {post.description}
                      </p>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </LandingLayout>
  );
}
